import { IPathNode, ISankeyLink, ISankeyNode } from './interfaces';
import { formatNumberWithComma } from '../../../utils/formatText';
import {
  defaultNumberFormatOptions,
  globalFormat,
} from '../../../utils/formatting';

export const isMergedNode = (node: IPathNode) => node.mergedNodes.length > 0;

const mergedLastComparator = (a: ISankeyNode, b: ISankeyNode) => {
  if (!isMergedNode(a) && isMergedNode(b)) {
    return -1;
  } else if (isMergedNode(a) && !isMergedNode(b)) {
    return 1;
  } else {
    return 0;
  }
};

const groupComparator = (a: ISankeyNode, b: ISankeyNode) =>
  a.groupIndex - b.groupIndex;

const scoreDescComparator = (a: ISankeyNode, b: ISankeyNode) =>
  b.nodeScore - a.nodeScore;

const sortOrder = [mergedLastComparator, scoreDescComparator, groupComparator];

export const nodeSort = (a: ISankeyNode, b: ISankeyNode) => {
  for (let sort of sortOrder) {
    const compResult = sort(a, b);
    if (compResult !== 0) {
      return compResult;
    }
  }
  return 0;
};

export const linkSort = (a: ISankeyLink, b: ISankeyLink) => {
  // links to Others node are always last
  if (isMergedNode(a.target)) {
    return 1;
  } else {
    return b.value - a.value;
  }
};

export const minDimension = (n: number) => {
  if (n < 1) {
    return 1;
  } else {
    return n;
  }
};

export const getNodeStats = (node: IPathNode) => {
  const {
    incomingCount,
    incomingTotalPerSeq,
    incomingCountFromOther,
    outgoingCount,
  } = node;
  const percent = `${globalFormat.formatNumber(
    incomingCount / incomingTotalPerSeq,
    defaultNumberFormatOptions.percent,
  )}`;

  const counts = `${globalFormat.formatNumber(incomingCount, defaultNumberFormatOptions.integer)}`;

  const tooltip: string[] = [];
  if (incomingCountFromOther > 0) {
    tooltip.push(
      `The value includes ${formatNumberWithComma(
        incomingCountFromOther,
      )} (${globalFormat.formatNumber(
        incomingCountFromOther / incomingTotalPerSeq,
        defaultNumberFormatOptions.percent,
      )}) subjects coming from the other previous steps.`,
    );
  }
  if (outgoingCount > incomingCount) {
    const extraSubjects = outgoingCount - incomingCount;
    tooltip.push(
      `The value does not include additional ${formatNumberWithComma(
        extraSubjects,
      )} (${globalFormat.formatNumber(
        extraSubjects / outgoingCount,
        defaultNumberFormatOptions.percent,
      )}) subjects that performed this step. ` +
        'The partition and attribution settings or the "long tail" influence this number.',
    );
  }

  return {
    counts,
    percent,
    tooltip: tooltip.length > 0 ? tooltip.join(' ') : undefined,
  };
};
